<template>
  <v-dialog
    v-model="dialog"
    persistent
    :fullscreen="isMobile"
    :max-width="isMobile ? undefined : '580px'"
    content-class="add-match-dialog"
    transition="dialog-transition"
  >
    <v-card class="add-match-card">
      <div class="add-match-header">
        <h2 class="add-match-title">
          {{ isFromHome ? "Add match" : "Add match for " + wrestlerName }}
        </h2>
        <v-btn icon dark class="add-match-close" @click="resetForm()">
          <v-icon>close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="add-match-body">
        <!-- Duplicate warning alert -->
        <v-alert
          v-if="duplicateErrors.length > 0"
          type="warning"
          dismissible
          class="duplicate-warning"
          :value="true"
        >
          <strong>Duplicate Match Found:</strong> {{ duplicateErrors[0] }}
        </v-alert>

        <div class="add-match-form">
          <!-- Wrestlers: full-width rows so labels/placeholders are not clipped -->
          <div class="form-row form-row-wrestlers">
            <div class="wrestler-pick-row">
              <div class="wrestler-pick-field">
                <v-text-field
                  :value="Wrestler ? Wrestler.text : ''"
                  label="Wrestler 1"
                  placeholder="Tap to choose"
                  outline
                  readonly
                  :error-messages="wrestler1Errors"
                  class="form-field wrestler-pick-input"
                  @click.native="openSearchFor('wrestler1')"
                />
                <v-btn
                  class="wrestler-pick-btn"
                  color="primary"
                  depressed
                  @click="openSearchFor('wrestler1')"
                >
                  Search
                </v-btn>
              </div>
            </div>
            <div class="wrestler-pick-row">
              <div class="wrestler-pick-field">
                <v-text-field
                  :value="Opponent ? Opponent.text : ''"
                  :label="isFromHome ? 'Wrestler 2' : 'Opponent'"
                  placeholder="Tap to choose"
                  outline
                  readonly
                  :error-messages="wrestler2Errors"
                  class="form-field wrestler-pick-input"
                  @click.native="openSearchFor('opponent')"
                />
                <v-btn
                  class="wrestler-pick-btn"
                  color="primary"
                  depressed
                  @click="openSearchFor('opponent')"
                >
                  Search
                </v-btn>
              </div>
            </div>
          </div>
          <wrestler-search-dialog
            :value="wrestlerSearchOpen"
            :exclude-id="wrestlerSearchExcludeId"
            @input="wrestlerSearchOpen = $event"
            @select="onWrestlerSearchSelect"
          />

          <!-- Row 2: Winner | Type -->
          <div class="form-row form-row-split">
            <div class="form-row-cell">
              <v-select
                v-model="Winner"
                :items="winnerOptions"
                item-text="text"
                item-value="value"
                label="Winner"
                placeholder="Who won?"
                outline
                required
                :error-messages="winnerErrors"
                class="form-field"
              />
            </div>
            <div class="form-row-cell">
              <v-select
                v-model="ResultType"
                :items="WinTypes"
                label="Type"
                outline
                class="form-field"
              />
            </div>
          </div>

          <!-- Row 3: Season | Date -->
          <div class="form-row form-row-split">
            <div class="form-row-cell">
              <v-select
                v-model="Season"
                :items="seasonOptions"
                label="Season"
                outline
                class="form-field"
              />
            </div>
            <div class="form-row-cell">
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="MatchDate"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
                content-class="add-match-date-menu"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="MatchDate"
                    label="Match date"
                    readonly
                    outline
                    class="form-field"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="MatchDate" no-title scrollable>
                  <v-spacer />
                  <v-btn flat color="primary" @click="menu = false"
                    >Cancel</v-btn
                  >
                  <v-btn
                    flat
                    color="primary"
                    @click="$refs.menu.save(MatchDate)"
                    >OK</v-btn
                  >
                </v-date-picker>
              </v-menu>
            </div>
          </div>

          <!-- Row 4: Location -->
          <div class="form-row">
            <div class="form-row-cell">
              <v-text-field
                v-model="MatchLocation"
                label="Location"
                outline
                required
                class="form-field"
              />
            </div>
          </div>

          <!-- Row 5: Video link -->
          <div class="form-row">
            <v-text-field
              v-model="MatchVideo"
              label="Video link (optional)"
              outline
              placeholder="https://..."
              class="form-field"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="add-match-actions">
        <v-btn flat class="action-btn action-cancel" @click="resetForm()">
          Cancel
        </v-btn>
        <v-spacer />
        <div class="add-match-actions-primary">
          <v-btn
            v-if="isFromHome"
            color="primary"
            class="action-btn action-save"
            @click="insertMultipleMatch()"
          >
            Save and add another
          </v-btn>
          <v-btn
            color="primary"
            class="action-btn action-save"
            @click="insertMatch()"
          >
            Save
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
//import arpad from 'arpad';
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  orderBy,
  query,
  writeBatch,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import Elo from "elo-js";
import { logAuditEntry } from "../utils/auditLog";
import {
  MATCHES_CANONICAL_COLLECTION,
  computeCanonicalMatchDocId,
  duplicateBoutEitherWinner,
  participantAthleteIds,
} from "../utils/canonicalMatches";
import { invalidateGlobalMaxSkillCache } from "../utils/skillBarLinear";
import { saveRankingsSnapshotForAthletes } from "../utils/rankingsSnapshot";
import {
  seasonLabelFromMatchDate,
  currentSeasonLabelShort,
  normalizeSeasonToShort,
} from "../utils/seasonFilter";
import WrestlerSearchDialog from "./WrestlerSearchDialog.vue";

export default {
  components: { WrestlerSearchDialog },
  props: {
    dialog: Boolean,
    wrestlerName: { type: [String, Number], default: "" },
    wrestlerID: { type: [String, Number], default: "0" },
    wrestlerSkill: { type: Number, default: 0 },
    wrestlerWins: { type: Number, default: 0 },
    wrestlerLosses: { type: Number, default: 0 },
    profileWrestler: { type: Object, default: null },
  },
  data: () => ({
    Winner: "",
    ResultType: "",
    MatchLocation: "",
    MatchVideo: "",
    Opponent: "",
    Wrestler: "",
    MatchDate: "",
    Season: "",
    menu: "",
    name: "kennected-add-match",
    WinTypes: [
      "Decision",
      "Major",
      "Technical Fall",
      "Fall",
      "Disqualification",
      "Forfeit",
      "Injury Default",
    ],
    weightclasses: [],
    OpponentList: [],
    validationErrors: [],
    isMobile: false,
    wrestlerSearchOpen: false,
    wrestlerSearchFor: null,
  }),
  computed: {
    wrestlerSearchExcludeId() {
      if (this.wrestlerSearchFor === "wrestler1")
        return this.Opponent && this.Opponent.value ? this.Opponent.value : "";
      if (this.wrestlerSearchFor === "opponent") {
        if (this.isFromHome && this.Wrestler && this.Wrestler.value)
          return this.Wrestler.value;
        if (!this.isFromHome && this.wrestlerID) return String(this.wrestlerID);
      }
      return "";
    },
    isFromHome() {
      const id = this.wrestlerID;
      return (
        id === undefined || id === null || id === 0 || id === "0" || id === ""
      );
    },
    wrestler1Errors() {
      if (!this.isFromHome) return [];
      return this.validationErrors
        .filter(function (e) {
          return e.field === "wrestler1";
        })
        .map(function (e) {
          return e.message;
        });
    },
    wrestler2Errors() {
      return this.validationErrors
        .filter(function (e) {
          return e.field === "wrestler2";
        })
        .map(function (e) {
          return e.message;
        });
    },
    winnerOptions() {
      if (this.isFromHome) {
        var w1 =
          this.Wrestler && this.Wrestler.text
            ? this.Wrestler.text
            : "Wrestler 1";
        var w2 =
          this.Opponent && this.Opponent.text
            ? this.Opponent.text
            : "Wrestler 2";
        return [
          { text: w1, value: "first" },
          { text: w2, value: "second" },
        ];
      }
      var profile =
        (this.Wrestler && this.Wrestler.text) ||
        this.wrestlerName ||
        "This wrestler";
      var opp =
        this.Opponent && this.Opponent.text ? this.Opponent.text : "Opponent";
      return [
        { text: profile, value: "first" },
        { text: opp, value: "second" },
      ];
    },
    winnerErrors() {
      return this.validationErrors
        .filter(function (e) {
          return e.field === "winner";
        })
        .map(function (e) {
          return e.message;
        });
    },
    duplicateErrors() {
      return this.validationErrors
        .filter(function (e) {
          return e.field === "duplicate";
        })
        .map(function (e) {
          return e.message;
        });
    },
    seasonOptions() {
      const opts = [];
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      for (let i = -2; i <= 1; i++) {
        const y = year + (month >= 6 ? i : i - 1);
        opts.push(`${y}-${String(y + 1).slice(-2)}`);
      }
      return opts;
    },
  },
  watch: {
    RankLeague: function (newVal) {
      // console.log("newVal.text => ", newVal.text, newVal.value);
      if (newVal > "") {
        this.loadWeights(newVal.value);
      }
      // console.log("weight classes => ", this.weightclasses);
    },

    dialog: function (newVal) {
      if (newVal == true) {
        this.validationErrors = [];
        if (!this.MatchDate || typeof this.MatchDate !== "string") {
          this.MatchDate = this.getTodayDateString();
        }
        this.Season = this.seasonFromMatchDateString(this.MatchDate);
        this.applyProfileWrestlerIfNeeded();
      }
    },
    wrestlerID: {
      handler() {
        if (this.dialog) this.applyProfileWrestlerIfNeeded();
      },
      immediate: false,
    },
    Wrestler: function () {
      this.validationErrors = this.validationErrors.filter(function (e) {
        return e.field !== "wrestler1";
      });
    },
    Opponent: function () {
      this.validationErrors = this.validationErrors.filter(function (e) {
        return e.field !== "wrestler2";
      });
    },
    Winner: function () {
      this.validationErrors = this.validationErrors.filter(function (e) {
        return e.field !== "winner";
      });
    },
    MatchDate: function (newVal) {
      this.validationErrors = this.validationErrors.filter(function (e) {
        return e.field !== "duplicate";
      });
      const s = this.seasonFromMatchDateString(newVal);
      if (s) this.Season = s;
    },
    ResultType: function () {
      this.validationErrors = this.validationErrors.filter(function (e) {
        return e.field !== "duplicate";
      });
    },
  },
  created() {
    this.MatchDate = this.getTodayDateString();
    this.Season = this.seasonFromMatchDateString(this.MatchDate);
  },
  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    getTodayDateString: function () {
      var d = new Date();
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, "0");
      var day = String(d.getDate()).padStart(2, "0");
      return year + "-" + month + "-" + day;
    },
    seasonFromMatchDateString(str) {
      if (!str || typeof str !== "string") return currentSeasonLabelShort();
      const d = new Date(str + "T12:00:00");
      if (Number.isNaN(d.getTime())) return currentSeasonLabelShort();
      return seasonLabelFromMatchDate(d) || currentSeasonLabelShort();
    },
    seasonStringForDateObj(d) {
      if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
      return seasonLabelFromMatchDate(d) || "";
    },
    /** Season stored on `matches_canonical`: UI picker when set, else scholastic year from match date. */
    seasonForCanonicalSave(matchDateObj) {
      const raw =
        this.Season != null && String(this.Season).trim() !== ""
          ? String(this.Season).trim()
          : "";
      if (raw) {
        const n = normalizeSeasonToShort(raw);
        if (n && n !== "All") return n;
      }
      const fromDate = this.seasonStringForDateObj(matchDateObj);
      if (!fromDate) return "";
      const n2 = normalizeSeasonToShort(String(fromDate).trim());
      return n2 && n2 !== "All" ? n2 : fromDate;
    },
    matchDateToDate: function () {
      if (!this.MatchDate || typeof this.MatchDate !== "string")
        return new Date();
      return new Date(this.MatchDate + "T12:00:00");
    },
    toTimestamp: function (strDate) {
      var datum = Date.parse(strDate);
      return datum / 1000;
    },
    applyProfileWrestlerIfNeeded() {
      if (this.profileWrestler && this.profileWrestler.value) {
        this.Wrestler = {
          text: this.profileWrestler.text || "Wrestler",
          value: this.profileWrestler.value,
          skill: this.profileWrestler.skill,
          wins: this.profileWrestler.wins,
          losses: this.profileWrestler.losses,
          image: this.profileWrestler.image || "",
        };
        return;
      }
      const raw = this.wrestlerID;
      const id =
        raw != null && String(raw).trim() !== "" && String(raw) !== "0"
          ? String(raw).trim()
          : "";
      if (id === "") return;
      const name =
        this.wrestlerName != null && String(this.wrestlerName).trim() !== ""
          ? String(this.wrestlerName).trim()
          : "Wrestler";
      this.Wrestler = {
        text: name,
        value: id,
        skill: this.wrestlerSkill,
        wins: this.wrestlerWins,
        losses: this.wrestlerLosses,
        image: "",
      };
    },
    openSearchFor(slot) {
      this.wrestlerSearchFor = slot;
      this.wrestlerSearchOpen = true;
    },
    onWrestlerSearchSelect(payload) {
      if (this.wrestlerSearchFor === "wrestler1") this.Wrestler = payload;
      else this.Opponent = payload;
      this.wrestlerSearchOpen = false;
      this.wrestlerSearchFor = null;
    },
    getOpponentFirestore: function () {
      const db = getFirestore();
      const q = query(collection(db, "athletes"), orderBy("FullName"));
      getDocs(q).then((querySnapshot) => {
        this.OpponentList = [];
        const seenNames = new Set();
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const name = data.FullName;
          if (seenNames.has(name)) return;
          seenNames.add(name);
          this.OpponentList.push({
            text: name,
            value: doc.id,
            skill: data.Skill,
            wins: data.Wins,
            losses: data.Losses,
            image: data.Image || "",
          });
        });
      });
    },

    resetForm: function () {
      this.Winner = "";
      this.ResultType = "";
      this.MatchLocation = "";
      this.MatchVideo = "";
      this.Opponent = "";
      this.Wrestler = "";
      this.WrestlerSkill = "";
      this.MatchDate = this.getTodayDateString();
      this.Season = this.seasonFromMatchDateString(this.MatchDate);
      this.validationErrors = [];
      this.$emit("close-addMatch");
    },
    resetFormForNext: function () {
      this.Winner = "";
      this.ResultType = "";
      this.MatchVideo = "";
      this.Opponent = "";
      this.Wrestler = "";
    },
    /* Derive Win/Loss from Winner: 'first' = wrestler1 won, 'second' = wrestler2 (opponent) won */
    wrestler1Won: function () {
      return this.Winner === "first";
    },

    validateForSave: function () {
      this.validationErrors = [];
      if (this.isFromHome) {
        if (!this.Wrestler || !this.Wrestler.value) {
          this.validationErrors.push({
            field: "wrestler1",
            message: "Please select the first wrestler.",
          });
        }
        if (!this.Opponent || !this.Opponent.value) {
          this.validationErrors.push({
            field: "wrestler2",
            message: "Please select the second wrestler.",
          });
        }
        if (
          this.Wrestler &&
          this.Opponent &&
          this.Wrestler.value === this.Opponent.value
        ) {
          this.validationErrors.push({
            field: "wrestler2",
            message: "Wrestler 1 and Wrestler 2 must be different people.",
          });
        }
      } else {
        if (!this.Opponent || !this.Opponent.value) {
          this.validationErrors.push({
            field: "wrestler2",
            message: "Please select the opponent.",
          });
        }
      }
      if (!this.Winner) {
        this.validationErrors.push({
          field: "winner",
          message: "Please select the winner.",
        });
      }
      return this.validationErrors.length === 0;
    },

    isDuplicateMatch: async function (
      wrestler1ID,
      opponentID,
      matchDateObj,
      resultType
    ) {
      try {
        const db = getFirestore();
        const ts = Timestamp.fromDate(matchDateObj);
        return await duplicateBoutEitherWinner(
          db,
          wrestler1ID,
          opponentID,
          ts,
          resultType
        );
      } catch (_err) {
        const msg =
          _err && _err.message
            ? _err.message
            : "Could not verify match history.";
        this.validationErrors.push({
          field: "duplicate",
          message: msg + " Please try again.",
        });
        return true;
      }
    },

    insertMatch: async function () {
      if (!this.validateForSave()) return;
      this.validationErrors = this.validationErrors.filter(
        (e) => e.field !== "duplicate"
      );

      try {
        var elo = new Elo();
        var matchDateObj = this.matchDateToDate();
        const db = getFirestore();
        var batch = writeBatch(db);

        var wrestler1ID = !this.isFromHome
          ? this.wrestlerID
          : this.Wrestler.value;
        wrestler1ID =
          wrestler1ID != null && wrestler1ID !== ""
            ? String(wrestler1ID).trim()
            : "";
        var wrestler1Skill = !this.isFromHome
          ? this.wrestlerSkill
          : this.Wrestler.skill;
        var wrestler1Wins = !this.isFromHome
          ? this.wrestlerWins
          : this.Wrestler.wins;
        var wrestler1Losses = !this.isFromHome
          ? this.wrestlerLosses
          : this.Wrestler.losses;

        // Store old values for audit logging
        var oldWrestler1Skill = wrestler1Skill;
        var oldWrestler1Wins = wrestler1Wins;
        var oldWrestler1Losses = wrestler1Losses;
        var oldWrestler2Skill = this.Opponent.skill;
        var oldWrestler2Wins = this.Opponent.wins;
        var oldWrestler2Losses = this.Opponent.losses;

        // Duplicate detection: same two wrestlers, same date, same type
        var opponentID = this.Opponent && this.Opponent.value;
        opponentID =
          opponentID != null && opponentID !== ""
            ? String(opponentID).trim()
            : "";
        var isDup = await this.isDuplicateMatch(
          wrestler1ID,
          opponentID,
          matchDateObj,
          this.ResultType
        );
        if (isDup) {
          this.validationErrors.push({
            field: "duplicate",
            message:
              "A match between these wrestlers with this date and type already exists.",
          });
          return;
        }

        // Ensure both wrestlers exist in the database before adding a match
        const wrestler1Ref = doc(db, "athletes", wrestler1ID);
        const opponentRef = doc(db, "athletes", opponentID);
        const [w1Snap, w2Snap] = await Promise.all([
          getDoc(wrestler1Ref),
          getDoc(opponentRef),
        ]);
        if (!w1Snap.exists()) {
          this.validationErrors.push({
            field: "wrestler1",
            message:
              "The first wrestler no longer exists. Please refresh and try again.",
          });
          return;
        }
        if (!w2Snap.exists()) {
          this.validationErrors.push({
            field: "wrestler2",
            message:
              "The opponent no longer exists. Please refresh and select another wrestler.",
          });
          return;
        }

        var wrestler1Won = this.wrestler1Won();
        var result1 = wrestler1Won ? "Win" : "Loss";
        var result2 = wrestler1Won ? "Loss" : "Win";

        var NewWrestler1Skill, NewWrestler2Skill;
        if (wrestler1Won) {
          NewWrestler1Skill = elo.ifWins(wrestler1Skill, this.Opponent.skill);
          NewWrestler2Skill = elo.ifLoses(this.Opponent.skill, wrestler1Skill);
          wrestler1Wins = wrestler1Wins + 1;
          this.Opponent.losses = this.Opponent.losses + 1;
        } else {
          NewWrestler1Skill = elo.ifLoses(wrestler1Skill, this.Opponent.skill);
          NewWrestler2Skill = elo.ifWins(this.Opponent.skill, wrestler1Skill);
          wrestler1Losses = wrestler1Losses + 1;
          this.Opponent.wins = this.Opponent.wins + 1;
        }

        var multiFactor = 0;
        if (this.ResultType == "Decision") {
          multiFactor = 1.0;
        } else if (this.ResultType == "Major") {
          multiFactor = 1.2;
        } else if (this.ResultType == "Technical Fall") {
          multiFactor = 1.2;
        } else if (this.ResultType == "Fall") {
          multiFactor = 1.3;
        }

        NewWrestler1Skill =
          wrestler1Skill + (NewWrestler1Skill - wrestler1Skill) * multiFactor;
        NewWrestler2Skill =
          this.Opponent.skill +
          (NewWrestler2Skill - this.Opponent.skill) * multiFactor;

        // we dont' want the skill level to go below 1
        if (NewWrestler1Skill < 1) {
          NewWrestler1Skill = 1;
        }
        if (NewWrestler2Skill < 1) {
          NewWrestler2Skill = 1;
        }

        // Calculate skill changes
        var skillChange1 =
          Math.round((NewWrestler1Skill - wrestler1Skill) * 100) / 100;
        var skillChange2 =
          Math.round((NewWrestler2Skill - this.Opponent.skill) * 100) / 100;

        var WName = this.isFromHome ? this.Wrestler.text : this.wrestlerName;
        var WImage = this.isFromHome
          ? (this.Wrestler && this.Wrestler.image) || ""
          : this.wrestlerImage || "";

        var matchTs = Timestamp.fromDate(matchDateObj);
        var winnerId = String(wrestler1Won ? wrestler1ID : opponentID).trim();
        var loserId = String(wrestler1Won ? opponentID : wrestler1ID).trim();
        var canonicalSeason = this.seasonForCanonicalSave(matchDateObj);
        var canonicalId = await computeCanonicalMatchDocId(
          winnerId,
          loserId,
          matchTs,
          this.ResultType
        );
        var canonicalRef = doc(db, MATCHES_CANONICAL_COLLECTION, canonicalId);
        var skillChangeWinner = wrestler1Won ? skillChange1 : skillChange2;
        var skillChangeLoser = wrestler1Won ? skillChange2 : skillChange1;
        var oppLabelWinner = wrestler1Won ? this.Opponent.text : WName;
        var oppLabelLoser = wrestler1Won ? WName : this.Opponent.text;

        batch.set(
          canonicalRef,
          {
            WinnerAthleteId: winnerId,
            LoserAthleteId: loserId,
            ParticipantAthleteIds: participantAthleteIds(winnerId, loserId),
            MatchDate: matchTs,
            Season: canonicalSeason,
            Type: this.ResultType,
            Location: this.MatchLocation,
            VideoLink: this.MatchVideo,
            SkillChangeWinner: skillChangeWinner,
            SkillChangeLoser: skillChangeLoser,
            OpponentLabelWinnerDoc: String(oppLabelWinner || "").trim(),
            OpponentLabelLoserDoc: String(oppLabelLoser || "").trim(),
          },
          { merge: true }
        );

        // Wait for batch to commit before updating wrestlers
        await batch.commit();
        invalidateGlobalMaxSkillCache();

        this.updateWrestler(
          wrestler1ID,
          wrestler1Wins,
          wrestler1Losses,
          NewWrestler1Skill,
          oldWrestler1Wins,
          oldWrestler1Losses,
          oldWrestler1Skill
        );
        this.updateWrestler(
          opponentID,
          this.Opponent.wins,
          this.Opponent.losses,
          NewWrestler2Skill,
          oldWrestler2Wins,
          oldWrestler2Losses,
          oldWrestler2Skill
        );

        // Log match creation for both wrestlers (entity id = canonical bout id)
        const matchData = {
          Location: this.MatchLocation,
          MatchDate: matchDateObj,
          Season: canonicalSeason,
          OpponentFullName: this.Opponent.text,
          Type: this.ResultType,
          VideoLink: this.MatchVideo,
        };

        const changes = {};
        Object.keys(matchData).forEach((key) => {
          changes[key] = { old: null, new: matchData[key] };
        });

        logAuditEntry(
          "match",
          canonicalId,
          "create",
          {
            ...changes,
            Result: { old: null, new: result1 },
            WrestlerID: { old: null, new: opponentID },
            OpponentName: { old: null, new: this.Opponent.text },
          },
          "system",
          "Match Entry"
        );

        logAuditEntry(
          "match",
          canonicalId,
          "create",
          {
            ...changes,
            Result: { old: null, new: result2 },
            WrestlerID: { old: null, new: wrestler1ID },
            OpponentName: { old: null, new: WName },
          },
          "system",
          "Match Entry"
        );

        this.$root.$emit("toast", "Match added successfully", "success");
        this.$root.$emit("rankster-seasons-changed");
        this.resetForm();
      } catch (err) {
        const msg = err && err.message ? err.message : "Failed to save match.";
        this.validationErrors.push({
          field: "duplicate",
          message: msg,
        });
        this.$root.$emit("toast", msg, "error");
      }
    },

    insertMultipleMatch: async function () {
      if (!this.validateForSave()) return;
      this.validationErrors = this.validationErrors.filter(
        (e) => e.field !== "duplicate"
      );

      try {
        var matchDateObj = this.matchDateToDate();
        const db = getFirestore();
        var batch = writeBatch(db);

        var wrestler1ID = !this.isFromHome
          ? this.wrestlerID
          : this.Wrestler.value;
        wrestler1ID =
          wrestler1ID != null && wrestler1ID !== ""
            ? String(wrestler1ID).trim()
            : "";
        var wrestler1Skill = !this.isFromHome
          ? this.wrestlerSkill
          : this.Wrestler.skill;
        var wrestler1Wins = !this.isFromHome
          ? this.wrestlerWins
          : this.Wrestler.wins;
        var wrestler1Losses = !this.isFromHome
          ? this.wrestlerLosses
          : this.Wrestler.losses;

        // Store old values for audit logging
        var oldWrestler1Skill = wrestler1Skill;
        var oldWrestler1Wins = wrestler1Wins;
        var oldWrestler1Losses = wrestler1Losses;
        var oldWrestler2Skill = this.Opponent.skill;
        var oldWrestler2Wins = this.Opponent.wins;
        var oldWrestler2Losses = this.Opponent.losses;

        // Duplicate detection also applies when inserting multiple matches
        var opponentID = this.Opponent && this.Opponent.value;
        opponentID =
          opponentID != null && opponentID !== ""
            ? String(opponentID).trim()
            : "";
        var isDup = await this.isDuplicateMatch(
          wrestler1ID,
          opponentID,
          matchDateObj,
          this.ResultType
        );
        if (isDup) {
          this.validationErrors.push({
            field: "duplicate",
            message:
              "A match between these wrestlers with this date and type already exists.",
          });
          return;
        }

        const wrestler1Ref = doc(db, "athletes", wrestler1ID);
        const opponentRef = doc(db, "athletes", opponentID);
        const [w1Snap, w2Snap] = await Promise.all([
          getDoc(wrestler1Ref),
          getDoc(opponentRef),
        ]);
        if (!w1Snap.exists()) {
          this.validationErrors.push({
            field: "wrestler1",
            message:
              "The first wrestler no longer exists. Please refresh and try again.",
          });
          return;
        }
        if (!w2Snap.exists()) {
          this.validationErrors.push({
            field: "wrestler2",
            message:
              "The opponent no longer exists. Please refresh and select another wrestler.",
          });
          return;
        }

        var wrestler1Won = this.wrestler1Won();
        var result1 = wrestler1Won ? "Win" : "Loss";
        var result2 = wrestler1Won ? "Loss" : "Win";

        var elo = new Elo();
        var multiFactor = 0;
        if (this.ResultType == "Decision") multiFactor = 1.0;
        else if (
          this.ResultType == "Major" ||
          this.ResultType == "Technical Fall"
        )
          multiFactor = 1.2;
        else if (this.ResultType == "Fall") multiFactor = 1.3;

        var NewWrestler1Skill, NewWrestler2Skill;
        if (wrestler1Won) {
          NewWrestler1Skill = elo.ifWins(wrestler1Skill, this.Opponent.skill);
          NewWrestler2Skill = elo.ifLoses(this.Opponent.skill, wrestler1Skill);
          wrestler1Wins += 1;
          this.Opponent.losses += 1;
        } else {
          NewWrestler1Skill = elo.ifLoses(wrestler1Skill, this.Opponent.skill);
          NewWrestler2Skill = elo.ifWins(this.Opponent.skill, wrestler1Skill);
          wrestler1Losses += 1;
          this.Opponent.wins += 1;
        }
        NewWrestler1Skill =
          wrestler1Skill + (NewWrestler1Skill - wrestler1Skill) * multiFactor;
        NewWrestler2Skill =
          this.Opponent.skill +
          (NewWrestler2Skill - this.Opponent.skill) * multiFactor;
        if (NewWrestler1Skill < 1) NewWrestler1Skill = 1;
        if (NewWrestler2Skill < 1) NewWrestler2Skill = 1;

        // Calculate skill changes
        var skillChange1 =
          Math.round((NewWrestler1Skill - wrestler1Skill) * 100) / 100;
        var skillChange2 =
          Math.round((NewWrestler2Skill - this.Opponent.skill) * 100) / 100;

        var WName = this.isFromHome ? this.Wrestler.text : this.wrestlerName;
        var WImage = this.isFromHome
          ? (this.Wrestler && this.Wrestler.image) || ""
          : this.wrestlerImage || "";

        var matchTs = Timestamp.fromDate(matchDateObj);
        var winnerId = String(wrestler1Won ? wrestler1ID : opponentID).trim();
        var loserId = String(wrestler1Won ? opponentID : wrestler1ID).trim();
        var canonicalSeason = this.seasonForCanonicalSave(matchDateObj);
        var canonicalId = await computeCanonicalMatchDocId(
          winnerId,
          loserId,
          matchTs,
          this.ResultType
        );
        var canonicalRef = doc(db, MATCHES_CANONICAL_COLLECTION, canonicalId);
        var skillChangeWinner = wrestler1Won ? skillChange1 : skillChange2;
        var skillChangeLoser = wrestler1Won ? skillChange2 : skillChange1;
        var oppLabelWinner = wrestler1Won ? this.Opponent.text : WName;
        var oppLabelLoser = wrestler1Won ? WName : this.Opponent.text;

        batch.set(
          canonicalRef,
          {
            WinnerAthleteId: winnerId,
            LoserAthleteId: loserId,
            ParticipantAthleteIds: participantAthleteIds(winnerId, loserId),
            MatchDate: matchTs,
            Season: canonicalSeason,
            Type: this.ResultType,
            Location: this.MatchLocation,
            VideoLink: this.MatchVideo,
            SkillChangeWinner: skillChangeWinner,
            SkillChangeLoser: skillChangeLoser,
            OpponentLabelWinnerDoc: String(oppLabelWinner || "").trim(),
            OpponentLabelLoserDoc: String(oppLabelLoser || "").trim(),
          },
          { merge: true }
        );

        // Wait for batch to commit before logging and updating
        await batch.commit();
        invalidateGlobalMaxSkillCache();

        // Log match creation for both wrestlers (entity id = canonical bout id)
        const matchData = {
          Location: this.MatchLocation,
          MatchDate: matchDateObj,
          Season: canonicalSeason,
          OpponentFullName: this.Opponent.text,
          Type: this.ResultType,
          VideoLink: this.MatchVideo,
        };

        const changes = {};
        Object.keys(matchData).forEach((key) => {
          changes[key] = { old: null, new: matchData[key] };
        });

        logAuditEntry(
          "match",
          canonicalId,
          "create",
          {
            ...changes,
            Result: { old: null, new: result1 },
            WrestlerID: { old: null, new: opponentID },
            OpponentName: { old: null, new: this.Opponent.text },
          },
          "system",
          "Match Entry"
        );

        logAuditEntry(
          "match",
          canonicalId,
          "create",
          {
            ...changes,
            Result: { old: null, new: result2 },
            WrestlerID: { old: null, new: wrestler1ID },
            OpponentName: { old: null, new: WName },
          },
          "system",
          "Match Entry"
        );

        // Save current rankings snapshot so rankings page can show ↑/↓ badges after skill change
        await saveRankingsSnapshotForAthletes(db, [
          wrestler1ID,
          opponentID,
        ]).catch(() => {});

        this.updateWrestler(
          wrestler1ID,
          wrestler1Wins,
          wrestler1Losses,
          NewWrestler1Skill,
          oldWrestler1Wins,
          oldWrestler1Losses,
          oldWrestler1Skill
        );
        this.updateWrestler(
          opponentID,
          this.Opponent.wins,
          this.Opponent.losses,
          NewWrestler2Skill,
          oldWrestler2Wins,
          oldWrestler2Losses,
          oldWrestler2Skill
        );
        this.$root.$emit("toast", "Match added successfully", "success");
        this.$root.$emit("rankster-seasons-changed");
        this.resetFormForNext();
      } catch (err) {
        const msg = err && err.message ? err.message : "Failed to save match.";
        this.validationErrors.push({ field: "duplicate", message: msg });
        this.$root.$emit("toast", msg, "error");
      }
    },

    insertWrestler: function () {
      const db = getFirestore();
      addDoc(collection(db, "athletes"), {
        FirstName: this.FirstName,
        LastName: this.LastName,
        RankWeightClass: this.RankWeightClass,
        RankLeague: this.RankLeague.text,
        School: this.School,
        Grade: this.Grade,
        Image: "",
        Skill: 1200,
        Wins: 0,
        Losses: 0,
      })
        .then(() => {
          this.$root.$emit("toast", "Wrestler added successfully", "success");
        })
        .catch((error) => {
          const msg =
            error && error.message ? error.message : "Failed to add wrestler.";
          this.$root.$emit("toast", msg, "error");
        });
      this.insertSchool(this.School);
      this.resetForm();
    },

    insertSchool: function (schoolName) {
      const db = getFirestore();
      const newSchoolRef = doc(collection(db, "schools"));
      setDoc(newSchoolRef, {
        Name: schoolName,
      }).catch(() => {});
    },
    updateWrestler: function (
      id,
      wins,
      losses,
      skill,
      oldWins = null,
      oldLosses = null,
      oldSkill = null
    ) {
      const db = getFirestore();
      const wrestlerRef = doc(db, "athletes", id);
      return updateDoc(wrestlerRef, {
        Skill: skill,
        Wins: wins,
        Losses: losses,
      }).catch((error) => {
        const msg =
          error && error.message
            ? error.message
            : "Failed to update wrestler record.";
        this.$root.$emit("toast", msg, "error");
      });
    },
  },
  asyncComputed: {},
};
</script>

<style scoped>
.add-match-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.add-match-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  color: #fff;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
}

.add-match-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.add-match-close {
  margin: 0;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.add-match-close:hover {
  opacity: 1;
}

.add-match-body {
  padding: 1.25rem 1.5rem 1.5rem;
}

.duplicate-warning {
  margin-bottom: 1rem;
  border-left: 4px solid #f57f17;
}

.add-match-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-row {
  width: 100%;
}

.form-row-wrestlers {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.wrestler-pick-row {
  width: 100%;
  min-width: 0;
}

.form-row-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}

.form-row-cell {
  min-width: 0;
}

.wrestler-pick-field {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.wrestler-pick-field .wrestler-pick-input {
  flex: 1;
  min-width: 0;
}
.wrestler-pick-btn {
  flex-shrink: 0;
  margin-top: 0;
  min-width: 92px !important;
  height: 40px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.form-field {
  width: 100%;
}

.add-match-actions {
  padding: 1rem 1.5rem 1.25rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.add-match-actions-primary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.action-btn {
  min-height: 40px;
  min-width: 100px;
  font-size: 0.95rem;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.action-cancel {
  color: #374151;
  border: 1px solid #d1d5db;
  background: white;
}

.action-cancel:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.action-save {
  background: #2563eb;
  color: white;
}

.action-save:hover {
  background: #1d4ed8;
}

/* Mobile: fullscreen dialog, stacked layout – match Add Wrestler flow */
@media (max-width: 767px) {
  .add-match-card {
    border-radius: 0;
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  .add-match-header {
    padding: 0.75rem 1rem;
    flex-shrink: 0;
  }

  .add-match-title {
    font-size: 1rem;
  }

  .add-match-body {
    padding: 1rem;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .add-match-form {
    gap: 0.5rem;
  }

  .form-row-split {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .add-match-actions {
    padding: 0.75rem 1rem;
    flex-direction: column-reverse;
    flex-shrink: 0;
    gap: 0.5rem;
  }

  .add-match-actions-primary {
    width: 100%;
    flex-direction: column;
    gap: 0.5rem;
  }

  .add-match-actions .action-btn {
    width: 100%;
    min-height: 44px;
    margin: 0;
  }

  .action-save {
    margin-left: 0;
  }
}
</style>

<style>
/* Add Match dialog: match Edit Wrestler UI - not scoped so we can override Vuetify */

.add-match-dialog .v-card {
  overflow: hidden;
}

.add-match-dialog .v-input {
  margin-bottom: 0;
}

.add-match-dialog .v-input__control {
  min-height: auto;
}

.add-match-dialog .v-text-field__details,
.add-match-dialog .v-select__details {
  display: none;
}

.add-match-dialog .v-messages {
  min-height: 0;
  display: none;
}

.add-match-dialog .v-input__slot {
  padding: 8px 12px;
  min-height: 44px;
}

/* Mobile: fullscreen dialog, scrollable form, touch-friendly controls */
@media (max-width: 767px) {
  .add-match-dialog {
    margin: 0;
    max-width: 100%;
    width: 100%;
  }
  .add-match-dialog .v-dialog {
    margin: 0;
    height: 100%;
    max-height: 100%;
  }
  .add-match-dialog .v-card {
    max-height: 100vh;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .add-match-dialog .v-card__text {
    overflow-y: auto;
    flex: 1;
    -webkit-overflow-scrolling: touch;
  }
  .add-match-dialog .v-card__actions .v-btn {
    min-height: 44px;
    padding: 0 20px;
  }
  .add-match-dialog .v-input__control {
    min-height: 44px !important;
    height: 44px !important;
    max-height: 44px !important;
  }
  .add-match-dialog .v-input__slot,
  .add-match-dialog .v-text-field--outline > .v-input__control > .v-input__slot,
  .add-match-dialog .v-select > .v-input__control > .v-input__slot {
    min-height: 44px !important;
    height: 44px !important;
    max-height: 44px !important;
  }
}
</style>
